/**
 * SlidingTabs — animated tab bar with a sliding background indicator.
 *
 * Two visual variants share the same physics (measure the active tab's
 * `offsetLeft + offsetWidth`, translate an absolutely-positioned indicator
 * to match, transition `left + width` over 300ms with our brand cubic-bezier):
 *
 *   • variant="pill" — full-tab dark-teal background pill (used by the
 *     Pricing page Monthly / Annual toggle). Tabs render on z-10 with text
 *     colors that crossfade in the last 100ms of the slide so the cream
 *     destination text appears exactly when the pill lands.
 *
 *   • variant="underline" — a 2px underline that slides between tabs
 *     (used by the Investor Dashboard tab bar). Same physics, just
 *     bottom-aligned instead of full-pill.
 *
 * Usage:
 *   <SlidingTabs
 *     tabs={[{ id: "monthly", label: "Monthly" }, { id: "annual", label: "Annual", badge: <Pill /> }]}
 *     active={billingInterval}
 *     onChange={setBillingInterval}
 *     variant="pill"
 *     testId="billing-interval"
 *   />
 *
 * Re-measures on:
 *   - selection change
 *   - first paint + next animation frame (web-fonts may shift widths)
 *   - window resize
 */
import { useLayoutEffect, useRef, useState } from "react";

export default function SlidingTabs({
    tabs,
    active,
    onChange,
    variant = "pill",
    testId,
    ariaLabel,
    className = "",
}) {
    const tabRefs = useRef({});
    const [indicator, setIndicator] = useState({ left: 0, width: 0, opacity: 0 });

    useLayoutEffect(() => {
        function measure() {
            const el = tabRefs.current[active];
            if (!el) return;
            setIndicator({
                left: el.offsetLeft,
                width: el.offsetWidth,
                opacity: 1,
            });
        }
        measure();
        const raf = requestAnimationFrame(measure);
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", measure);
        };
    }, [active, tabs]);

    if (variant === "underline") {
        return (
            <div
                className={`relative flex flex-wrap gap-1 border-b border-bl ${className}`}
                data-testid={testId}
                role="tablist"
                aria-label={ariaLabel}
            >
                <span
                    aria-hidden
                    className="absolute bottom-0 h-0.5 bg-bl-primary transition-[left,width] duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] pointer-events-none"
                    style={indicator}
                />
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        ref={(el) => { tabRefs.current[t.id] = el; }}
                        type="button"
                        role="tab"
                        aria-selected={active === t.id}
                        onClick={() => onChange(t.id)}
                        className={
                            "px-4 py-2.5 text-sm transition-colors duration-200 delay-100 -mb-px " +
                            (active === t.id
                                ? "text-bl-primary font-medium"
                                : "text-bl-muted hover:text-bl")
                        }
                        data-testid={testId ? `${testId}-${t.id}` : undefined}
                    >
                        {t.label}
                        {t.badge}
                    </button>
                ))}
            </div>
        );
    }

    // pill variant
    return (
        <div
            className={`relative inline-flex items-center gap-1 p-1 rounded-full bg-bl-bg border border-bl ${className}`}
            data-testid={testId}
            role="tablist"
            aria-label={ariaLabel}
        >
            <span
                aria-hidden
                className="absolute top-1 bottom-1 rounded-full bg-bl-primary shadow-sm transition-[left,width] duration-300 ease-[cubic-bezier(0.22,0.61,0.36,1)] pointer-events-none"
                style={indicator}
            />
            {tabs.map((t) => (
                <button
                    key={t.id}
                    ref={(el) => { tabRefs.current[t.id] = el; }}
                    type="button"
                    role="tab"
                    aria-selected={active === t.id}
                    onClick={() => onChange(t.id)}
                    className={
                        "relative z-10 px-4 py-2.5 sm:py-1.5 min-h-[44px] sm:min-h-0 rounded-full text-sm transition-colors duration-100 delay-200 inline-flex items-center gap-2 " +
                        (active === t.id
                            ? "text-bl-cream font-medium"
                            : "text-bl-primary hover:text-bl")
                    }
                    data-testid={testId ? `${testId}-${t.id}` : undefined}
                >
                    {t.label}
                    {t.badge}
                </button>
            ))}
        </div>
    );
}
